<template>
  <b-form-input
    :id="inputId"
    v-model="local_input_value"
    type="text"
    inputmode="decimal"
    :disabled="isDisabled"
    :placeholder="computedPlaceholder"
    @mousewheel.native="$event.preventDefault()"
    @input="onInput"
    @focus="onFocus"
    @blur="onBlur"
  />
</template>

<script>
export default {
  name: 'NumberInput',

  model: { prop: 'value', event: 'input' },

  props: {
    // Parent v-model. Best as Number|null (string allowed for backward compatibility)
    value: { type: [String, Number, null], default: null },

    inputId: { type: String, default: null },
    isDisabled: { type: Boolean, default: false },

    // Optional: force a locale (e.g. "es-ES"). If omitted, uses browser locale.
    locale: { type: String, default: undefined },

    // money-like behavior
    decimals: { type: Number, default: 2 },
    useGrouping: { type: Boolean, default: true },

    placeholder: { type: String, default: '' },

    // If true, emit string like "12345.02" instead of Number 12345.02
    emitString: { type: Boolean, default: false },

    // If true, on focus remove grouping and show an editable fixed-decimal representation
    unformatOnFocus: { type: Boolean, default: true },
  },

  data() {
    return {
      local_input_value: '',
      lastValidInput: '',
      isFocused: false,
    }
  },

  computed: {
    computedPlaceholder() {
      if (this.placeholder) return this.placeholder

      const parts = new Intl.NumberFormat(this.locale).formatToParts(1.1)
      const dec = parts.find((p) => p.type === 'decimal')?.value || '.'
      // "0.00" or "0,00" depending on locale + decimals
      return `0${dec}${'0'.repeat(this.decimals)}`
    },
  },

  watch: {
    value() {
      // don’t overwrite what the user is typing
      if (!this.isFocused) this.syncFromValue()
    },
    locale() {
      if (!this.isFocused) this.syncFromValue()
    },
    decimals() {
      if (!this.isFocused) this.syncFromValue()
    },
    useGrouping() {
      if (!this.isFocused) this.syncFromValue()
    },
  },

  created() {
    this.syncFromValue()
  },

  methods: {
    // ---------- Formatting (browser locale unless `locale` prop is given) ----------
    formatAmount(n) {
      const num = Number(n)
      if (!Number.isFinite(num)) return ''
      return new Intl.NumberFormat(this.locale, {
        minimumFractionDigits: this.decimals,
        maximumFractionDigits: this.decimals,
        useGrouping: this.useGrouping,
      }).format(num)
    },

    getLocaleDecimalSeparator() {
      const parts = new Intl.NumberFormat(this.locale).formatToParts(1.1)
      return parts.find((p) => p.type === 'decimal')?.value || '.'
    },

    // No grouping, but uses locale decimal separator, fixed decimals
    formatEditable(n) {
      const num = Number(n)
      if (!Number.isFinite(num)) return ''
      const dec = this.getLocaleDecimalSeparator()
      const fixed = num.toFixed(this.decimals) // e.g. "12345.20"
      return dec === '.' ? fixed : fixed.replace('.', dec)
    },

    roundToDecimals(n) {
      const factor = Math.pow(10, this.decimals)
      return Math.round((n + Number.EPSILON) * factor) / factor
    },

    // ---------- Parsing (accepts either ',' or '.' as decimal separator) ----------
    parseUserNumber(input) {
      if (input == null) return null

      let s = String(input).trim()
      if (!s) return null

      // remove spaces and NBSP (group separators in some locales)
      s = s.replace(/[\s\u00A0]/g, '')

      // sign
      let sign = 1
      if (s.startsWith('-')) {
        sign = -1
        s = s.slice(1)
      }
      if (s.includes('-')) return null

      // decimal separator = last '.' or ',' typed
      const lastDot = s.lastIndexOf('.')
      const lastComma = s.lastIndexOf(',')
      const decPos = Math.max(lastDot, lastComma)

      let intPart = s
      let fracPart = ''

      if (decPos !== -1) {
        intPart = s.slice(0, decPos)
        fracPart = s.slice(decPos + 1)
      }

      // strip any remaining separators (grouping, extra punctuation)
      intPart = intPart.replace(/[.,]/g, '')
      fracPart = fracPart.replace(/[.,]/g, '')

      // must be digits only now
      if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart)) return null
      if (intPart === '' && fracPart === '') return null

      const normalized = fracPart ? `${intPart}.${fracPart}` : intPart
      const num = Number(normalized)

      return Number.isFinite(num) ? sign * num : null
    },

    // ---------- Sync local display from external v-model ----------
    syncFromValue() {
      if (this.value === null || this.value === undefined || this.value === '') {
        this.local_input_value = ''
        this.lastValidInput = ''
        return
      }

      const num = Number(this.value)
      const formatted = Number.isFinite(num) ? this.formatAmount(num) : ''

      this.local_input_value = formatted
      this.lastValidInput = formatted
    },

    // ---------- Events ----------
    onInput(val) {
      // Allow typing only digits + optional leading '-' + one decimal part using '.' or ','
      // (No grouping while typing; grouping is applied on blur.)
      const regex = /^-?[0-9]*([.,][0-9]*)?$/

      if (!regex.test(val)) {
        // b-form-input already applied the invalid char, so revert next tick
        this.$nextTick(() => {
          this.local_input_value = this.lastValidInput
        })
        return
      }

      // valid input => remember it
      this.lastValidInput = val
    },

    onFocus() {
      this.isFocused = true

      if (!this.unformatOnFocus) return

      // ✅ IMPORTANT: treat null/undefined/"" as empty (do not convert to 0)
      if (this.value === null || this.value === undefined || this.value === '') {
        // keep it empty
        this.local_input_value = ''
        this.lastValidInput = ''
        return
      }

      const num = Number(this.value)
      if (Number.isFinite(num)) {
        const editable = this.formatEditable(num)
        this.local_input_value = editable
        this.lastValidInput = editable
      }
    },

    onBlur() {
      this.isFocused = false

      const parsed = this.parseUserNumber(this.local_input_value)

      // empty => emit null + clear
      if (parsed === null && String(this.local_input_value).trim() === '') {
        this.$emit('input', null)
        this.local_input_value = ''
        this.lastValidInput = ''
        return
      }

      // invalid but not empty => revert to model
      if (parsed === null) {
        this.syncFromValue()
        return
      }

      // valid => round + emit + format for display
      const rounded = this.roundToDecimals(parsed)

      if (this.emitString) {
        this.$emit('input', rounded.toFixed(this.decimals)) // "12345.02"
      } else {
        this.$emit('input', rounded) // 12345.02
      }

      const formatted = this.formatAmount(rounded)
      this.local_input_value = formatted
      this.lastValidInput = formatted
    },
  },
}
</script>
